import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import { AlertTriangle, ShieldOff, ShieldAlert } from 'lucide-react';

/**
 * ModerationGuard - Prevents access for suspended/banned users
 * Checks user moderation status and displays block message
 */
const ModerationGuard = ({ children }) => {
  const { user, userProfile, logout } = useAuth();
  const [moderationStatus, setModerationStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkModerationStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check user_moderation_status table
        const { data, error } = await supabase
          .from('user_moderation_status')
          .select('status, reason, expires_at, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error checking moderation status:', error);
          setLoading(false);
          return;
        }

        // If user is suspended or banned
        if (data && (data.status === 'suspended' || data.status === 'banned')) {
          // Check if suspension has expired
          if (data.status === 'suspended' && data.expires_at) {
            const expiresAt = new Date(data.expires_at);
            if (new Date() > expiresAt) {
              // Suspension expired - auto-reactivate
              console.log('Suspension expired, auto-reactivating user');
              await supabase
                .from('user_moderation_status')
                .upsert({
                  user_id: user.id,
                  status: 'active',
                  reason: 'Suspension period expired',
                  updated_at: new Date().toISOString()
                }, { onConflict: 'user_id' });

              // Update is_active in user_profiles
              await supabase
                .from('user_profiles')
                .update({ is_active: true })
                .eq('id', user.id);

              setModerationStatus(null);
              setLoading(false);
              return;
            }
          }

          setModerationStatus(data);
        } else {
          setModerationStatus(null);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error in moderation check:', error);
        setLoading(false);
      }
    };

    checkModerationStatus();
  }, [user]);

  // Show loading while checking
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If user is suspended or banned, show block message
  if (moderationStatus && (moderationStatus.status === 'suspended' || moderationStatus.status === 'banned')) {
    const isSuspended = moderationStatus.status === 'suspended';
    const isBanned = moderationStatus.status === 'banned';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            {isSuspended && (
              <div className="bg-orange-100 p-3 rounded-full">
                <ShieldAlert className="w-12 h-12 text-orange-600" />
              </div>
            )}
            {isBanned && (
              <div className="bg-red-100 p-3 rounded-full">
                <ShieldOff className="w-12 h-12 text-red-600" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center mb-2">
            {isSuspended ? 'Account Suspended' : 'Account Banned'}
          </h1>

          {/* Message */}
          <div className={`${isSuspended ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-4`}>
            <p className="text-gray-700 text-center mb-2">
              {isSuspended 
                ? 'Your account has been temporarily suspended due to a violation of our Community Guidelines.'
                : 'Your account has been permanently banned due to serious violations of our Terms of Service.'}
            </p>
            
            {moderationStatus.reason && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-600 mb-1">Reason:</p>
                <p className="text-sm text-gray-700">{moderationStatus.reason}</p>
              </div>
            )}

            {isSuspended && moderationStatus.expires_at && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-600 mb-1">Expires:</p>
                <p className="text-sm text-gray-700">
                  {new Date(moderationStatus.expires_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="space-y-2">
            <a
              href="https://vasprouts.com/community-guidelines"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded transition-colors"
            >
              Review Community Guidelines
            </a>

            <a
              href="/support"
              className="block text-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Submit Appeal
            </a>

            <button
              onClick={logout}
              className="block w-full text-center bg-white hover:bg-gray-50 text-gray-600 font-medium py-2 px-4 rounded border border-gray-300 transition-colors"
            >
              Sign Out
            </button>
          </div>

          {/* Support contact */}
          <p className="text-center text-sm text-gray-500 mt-4">
            Questions? Contact{' '}
            <a href="mailto:support@vasprouts.com" className="text-blue-600 hover:underline">
              support@vasprouts.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  // User is not suspended/banned - render children normally
  return <>{children}</>;
};

export default ModerationGuard;
